import { useState, useEffect } from 'react'
import { doc, onSnapshot, Timestamp } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import type { ClientRegistry } from '@/types/models'
import { generateRegistryId } from '@/lib/clientRegistryData'

export function useRealtimeClientRegistry(artistId: string | null, clientId: string | null) {
  const [registry, setRegistry] = useState<ClientRegistry | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!artistId || !clientId) {
      setLoading(false)
      setRegistry(null)
      return
    }

    setLoading(true)
    const registryId = generateRegistryId(artistId, clientId)
    const registryRef = doc(db, 'clientRegistries', registryId)

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      registryRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data()
          const registryData: ClientRegistry = {
            id: snapshot.id,
            artistId: data.artistId,
            userId: data.userId,
            createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
            updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt,
            tattooCount: data.tattooCount,
            lastTattooDate: data.lastTattooDate instanceof Timestamp ? data.lastTattooDate.toDate() : data.lastTattooDate,
            bookingId: data.bookingId,
            chatId: data.chatId,
            tattoos: (data.tattoos || []).map((tattoo: any) => ({
              ...tattoo,
              createdAt: tattoo.createdAt instanceof Timestamp ? tattoo.createdAt.toDate() : new Date(tattoo.createdAt),
              freshPhotos: (tattoo.freshPhotos || []).map((photo: any) => ({
                ...photo,
                uploadedAt: photo.uploadedAt instanceof Timestamp ? photo.uploadedAt.toDate() : new Date(photo.uploadedAt),
              })),
              healedPhotos: (tattoo.healedPhotos || []).map((photo: any) => ({
                ...photo,
                uploadedAt: photo.uploadedAt instanceof Timestamp ? photo.uploadedAt.toDate() : new Date(photo.uploadedAt),
              })),
            })),
          }
          setRegistry(registryData)
          setError(null)
        } else {
          setRegistry(null)
        }
        setLoading(false)
      },
      (err) => {
        console.error('Error setting up real-time listener for client registry:', err)
        setError('Failed to load client information')
        setLoading(false)
      }
    )

    // Clean up listener on unmount or when dependencies change
    return () => {
      unsubscribe()
    }
  }, [artistId, clientId])

  return { registry, loading, error }
}

